import { useState, Fragment } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useActivePlayer } from '@/context/ActivePlayerContext';
import { useBackgroundSync } from '@/context/BackgroundSyncContext';
import { useToast } from '@/context/ToastContext';
import EditSet from './EditSet';
import FinalHeadWeightModal from './FinalHeadWeightModal';
import DeleteSetModal from './DeleteSetModal';
import { Accordion, AccordionShell, ControlledAccordion } from '@/common/Accordion';
import InTheBag, { SetClubs } from '@/common/InTheBag';
import Tile from '@/common/Tile';
import { SetMenu } from '@/common/PopoverMenu';
import DeleteModal from '@/common/DeleteModal';
import { TourBagService } from '@/service';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import { TourBagIronDetail, TourBagIronSet, TourBagMstrPayload } from '@/api/models';
import { buildSetSharedProps } from './helpers';
import { createEmptyTourBag, createDeletedTourBagIronDetail, isEmptyTile, isMobileResolution, normalizeString } from '@/utils';
import { IUseEditIronResult } from './useEditIron';
import { calculateFinalHeadWeight } from './caluclateFinalHeadWeight';
import { PageReadability } from '@/library/TourPageSettingsHelper';
import { isFieldVisible } from '@/library/FieldVisibilityHelper';
import clubStyles from '../Club.module.scss';
import setStyles from '../Set.module.scss';
import ClubHistory from '../ClubHistory/ClubHistory';
import { headTypeMap, Action } from '@/constants';
import OfflineModal from '@/common/OfflineModal';
import Linkify from 'linkify-react';
import PhotoCaptureSection from '@/common/PhotoCaptureSection';

interface IRenderIronInSetProps {
  iron: TourBagIronDetail;
  set: TourBagIronSet;
  options?: {
    showMenu: boolean;
    index: number;
    /* eslint-disable no-unused-vars */
    onRemoveFromSet: (set: TourBagIronSet, iron: TourBagIronDetail) => void;
    onDelete: (set: TourBagIronSet, iron: TourBagIronDetail) => void;
    onFinalHWClick: (iron: TourBagIronDetail) => void;
    onShowHistory: (iron: TourBagIronDetail) => void;
    /* eslint-enable no-unused-vars */
    showDivisionLine: boolean;
    pageReadability: PageReadability | undefined;
    defaultIcon?: boolean;
    showHistory?: boolean;
  };
}

const renderIronInSet = ({ iron, set, options }: IRenderIronInSetProps) => {
  const loftValue = `${iron.loftDigit}${iron.loftFraction}`;
  const lieValue = `${iron.lieDigit}${iron.lieFraction}`;
  const ironLengthValue = `${iron.shaftLengthDigit} ${iron.shaftLengthFraction}`;
  const finalHeadWeight = calculateFinalHeadWeight({
    headWeight: iron.headWeight,
    drill: iron.drill,
    tipPlug: iron.tipPlug,
    glueWeight: iron.glueWeight,
    leadTape: iron.leadTape,
  });
  const isCustomShaft = set.shaftName !== iron.shaftName;
  // Old system headModelName included familyName but now we don't, we check to remove duplicated familyName
  let ironHeadModel = iron.headModelName.startsWith(iron.familyName) ? iron.headModelName : `${iron.familyName} ${iron.headModelName}`;
  if (options?.pageReadability === 'limitedView') ironHeadModel = iron.headModelAlternateName || ironHeadModel;

  return (
    <Fragment key={iron.tourBagIronDetailId || uuidv4()}>
      <div className={setStyles.setDivisionLine} />
      <div className={`${clubStyles.tileRow} ${setStyles.setRow}`}>
        <div className={setStyles.setClub}>
          {isFieldVisible('Iron', 'ironClubName', options?.pageReadability) && <span>{iron.ironClubName}</span>}
          <span>{normalizeString(ironHeadModel)}</span>
        </div>
        <Tile label="Family" value={normalizeString(iron.familyName)} variant="large" />
        {isFieldVisible('Iron', 'loft', options?.pageReadability) && <Tile label="Loft" value={normalizeString(loftValue)} />}
        {isFieldVisible('Iron', 'lie', options?.pageReadability) && <Tile label="Lie" value={normalizeString(lieValue)} />}
        {isFieldVisible('Iron', 'shaftLength', options?.pageReadability) && <Tile label="Length" value={normalizeString(ironLengthValue)} />}
        {isFieldVisible('Iron', 'finalHW', options?.pageReadability) && (
          <>
            {options?.onFinalHWClick && (
              <span className={clubStyles.finalHWWrapper} role="button" onClick={() => options?.onFinalHWClick(iron)}>
                <Tile label="Final HW" value={normalizeString(finalHeadWeight?.toString())} tileStyle={clubStyles.finalHW} />
              </span>
            )}
            {!options?.onFinalHWClick && <Tile label="Final HW" value={normalizeString(finalHeadWeight?.toString())} tileStyle={clubStyles.finalHW} />}
          </>
        )}
        <InTheBag isActive={iron.isActive} className={clubStyles.inTheBag} />
        {options?.showMenu && options?.pageReadability === 'full' && (
          <SetMenu
            index={options?.index}
            defaultIcon={false} // Show specific icon for irons within sets
            onRemoveFromSet={() => options?.onRemoveFromSet(set, iron)}
            onDelete={() => options?.onDelete(set, iron)}
            onShowHistory={() => options?.onShowHistory(iron)}
            showHistory={options?.showHistory ?? false} // Added showHistory prop
          />
        )}
      </div>
      {isCustomShaft && (
        <div key={iron.tourBagIronDetailId} className={`${clubStyles.tileRow} ${setStyles.setCustomShaft}`}>
          <div>
            <span>Shaft</span>
            <span>{normalizeString(iron.shaftName)}</span>
          </div>
        </div>
      )}
      {options?.showDivisionLine && <div className={setStyles.setDivisionLine} />}
    </Fragment>
  );
};

interface IRenderSetProps {
  set: TourBagIronSet;
  pageReadability: PageReadability | undefined;
}

const renderSet = ({ set, pageReadability }: IRenderSetProps) => {
  return (
    <>
      {/* Set Properties */}
      <div className={clubStyles.category}>
        <div className={clubStyles.categoryName}>Set Properties</div>
      </div>
      {isFieldVisible('Utility', 'Iron', pageReadability) && (
        <div className={clubStyles.tileRow}>
          <Tile label="Hand" value={set.hand} />
        </div>
      )}

      {/* Set-level Shaft */}
      {isFieldVisible('Iron', 'shaftName', pageReadability) && (
        <div className={clubStyles.category}>
          <div className={clubStyles.categoryName}>Set Shaft</div>
          <div className={`${clubStyles.categoryTitle} ${isEmptyTile(set.shaftName) ? clubStyles.emptyValue : ''}`}>{normalizeString(set.shaftName)}</div>
        </div>
      )}
      <div className={clubStyles.tileRow}>
        {isFieldVisible('Iron', 'measured', pageReadability) && <Tile label="Measured" value={set.isShaftLengthFinished ? 'Finished' : 'Cut'} />}
        {isFieldVisible('Iron', 'cosmetic', pageReadability) && <Tile label="Cosmetic" value={normalizeString(set.shaftCosmetic)} variant="large" />}
        {isFieldVisible('Iron', 'swingWt', pageReadability) && <Tile label="Swing Wt" value={`${set.swingWtPrefixName}${set.swingWtSuffixName}`} />}
      </div>

      {/* Set-level Grip */}
      {isFieldVisible('Iron', 'gripType', pageReadability) && (
        <div className={clubStyles.category}>
          <div className={clubStyles.categoryName}>Set Grip</div>
          <div className={`${clubStyles.categoryTitle} ${isEmptyTile(set.gripType) ? clubStyles.emptyValue : ''}`}>{normalizeString(set.gripType)}</div>
        </div>
      )}
      <div className={clubStyles.tileRow}>
        {isFieldVisible('Iron', 'gripSize', pageReadability) && <Tile label="Grip Size" value={normalizeString(set.gripSize)} variant="large" />}
        {isFieldVisible('Iron', 'rndRib', pageReadability) && <Tile label="Round/Rib" value={normalizeString(set.rndRib)} />}
        {isFieldVisible('Iron', 'logo', pageReadability) && <Tile label="Logo" value={normalizeString(set.logo)} />}
        {isFieldVisible('Iron', 'gripTape', pageReadability) && <Tile label="Grip Tape" value={normalizeString(set.tape)} />}
      </div>

      {/* Set-level Notes */}
      {isFieldVisible('Iron', 'notes', pageReadability) && (
        <>
          <div className={clubStyles.category}>
            <div className={clubStyles.categoryName}>Set Notes</div>
          </div>
          <div className={clubStyles.tileRow}>
            <div className={`${clubStyles.notes} ${isEmptyTile(set.notes) ? clubStyles.emptyValue : ''}`}>
              <Linkify options={{ target: '_blank' }}>{normalizeString(set.notes)}</Linkify>
            </div>
          </div>
        </>
      )}
    </>
  );
};

interface IIronSetReadOnlyProps {
  ironSets: TourBagIronSet[];
  currentAccordionId: number;
  showCheckbox: boolean;
  /* eslint-disable no-unused-vars */
  isClubSelected: (id: number) => boolean;
  handleOpenAccordion: (id: number) => void;
  handleCheckboxEvent: (id: number, isChecked: boolean) => void;
  pageReadability?: PageReadability | undefined;
  /* eslint-enable no-unused-vars */
}

export const IronSetReadOnly = ({ ironSets, currentAccordionId, showCheckbox, isClubSelected, handleOpenAccordion, handleCheckboxEvent, pageReadability }: IIronSetReadOnlyProps) => {
  const [finalHWIron, setFinalHWIron] = useState<TourBagIronDetail | null>(null);

  return (
    <>
      {ironSets
        .sort((setA, setB) => setA.tourBagIronSetName.localeCompare(setB.tourBagIronSetName))
        .sort((setA, setB) => {
          const setAHasActive = setA.tourBagIronDetails.some((iron) => iron.isActive);
          const setBHasActive = setB.tourBagIronDetails.some((iron) => iron.isActive);
          if (setAHasActive === setBHasActive) return 0; // Put sets with active irons first
          return setAHasActive ? -1 : 1;
        })
        .map((set) => {
          const setClubsCount = set.tourBagIronDetails.length;
          const inBagCount = set.tourBagIronDetails.filter((iron) => iron.isActive).length;

          return (
            <ControlledAccordion
              key={set.tourBagIronSetId}
              id={set.tourBagIronSetId}
              header={
                <>
                  <div className={setStyles.headerRow}>
                    <span className={setStyles.setModel}>{set.tourBagIronSetName}</span>
                    <span className={setStyles.dot}>·</span>
                    <span className={setStyles.clubCount}>
                      {setClubsCount} {setClubsCount === 1 ? 'club' : 'clubs'}
                    </span>
                    {!isMobileResolution && <SetClubs amount={inBagCount} squareIcon={!inBagCount} className={`${clubStyles.inTheBag} ${setStyles.inBag}`} />}
                  </div>
                  {isMobileResolution && <SetClubs amount={inBagCount} squareIcon={!inBagCount} className={`${clubStyles.inTheBag} ${setStyles.inBag}`} />}
                </>
              }
              isOpen={currentAccordionId === set.tourBagIronSetId}
              showCheckbox={showCheckbox}
              checked={isClubSelected(set.tourBagIronSetId)}
              handleOpenAccordion={() => handleOpenAccordion(set.tourBagIronSetId)}
              handleCheckboxEvent={handleCheckboxEvent}
            >
              {/* Render irons inside the set */}
              {set.tourBagIronDetails
                .sort((a, b) => (a.loftDigit + a.loftFraction).localeCompare(b.loftDigit + b.loftFraction))
                .sort((a, b) => Number(b.isActive) - Number(a.isActive))
                .map((iron) => {
                  return renderIronInSet({
                    iron,
                    set,
                    options: {
                      pageReadability,
                      showMenu: false,
                      index: 0,
                      onRemoveFromSet: () => {},
                      onDelete: () => {},
                      onFinalHWClick: setFinalHWIron,
                      onShowHistory: () => {},
                      showDivisionLine: false,
                    },
                  });
                })}

              {renderSet({ set, pageReadability })}
            </ControlledAccordion>
          );
        })}
      {finalHWIron && <FinalHeadWeightModal showFinalHWModal closeFinalHWModal={() => setFinalHWIron(null)} iron={finalHWIron} />}
    </>
  );
};

/* eslint-disable no-unused-vars */
interface IIronSetProps extends IUseEditIronResult {
  showClubs: boolean;
  setShowSpinner: (state: boolean) => void;
  ironSetToCreate: TourBagIronSet | null;
  setIronSetToCreate: (ironSet: TourBagIronSet | null) => void;
  ironSetToEdit: TourBagIronSet | null;
  setIronSetToEdit: (ironSet: TourBagIronSet | null) => void;
}
/* eslint-enable no-unused-vars */

function IronSet({
  showClubs,
  setShowSpinner,
  ironSetToCreate,
  setIronSetToCreate,
  ironSetToEdit,
  setIronSetToEdit,
  ironClubData,
  loftDropdownData,
  lieDropdownData,
  ironStaticDropdownData,
  ironDynamicDropdownData,
  shaftDropdownData,
  shaftLengthDropdownData,
  swingWtDropdownData,
  handDropdownData,
  roundRibDropdownData,
  logoDropdownData,
}: IIronSetProps) {
  const { tourBag, player, updateTourBag, permissions } = useActivePlayer();
  const tourPageSettings = permissions?.tourPageSettings;
  const { isSyncComplete } = useBackgroundSync();
  const { showToast } = useToast();
  const isOnline = useOnlineStatus();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [ironToDelete, setIronToDelete] = useState<{ tourBagIronSetId: number; tourBagIronDetailId: number } | null>(null);
  const [showRemoveFromSetModal, setShowRemoveFromSetModal] = useState(false);
  const [ironToRemove, setIronToRemove] = useState<{ tourBagIronSetId: number; tourBagIronDetailId: number } | null>(null);
  const [ironForHistory, setIronForHistory] = useState<TourBagIronDetail | null>(null);
  const [showFinalHWModal, setShowFinalHWModal] = useState(false);
  const [finalHWIron, setFinalHWIron] = useState<TourBagIronDetail | null>(null);
  const [showDeleteSetModal, setShowDeleteSetModal] = useState(false);
  const [setToDelete, setSetToDelete] = useState<TourBagIronSet | null>(null);
  const ironBag = tourBag?.find((bag) => bag.headTypeDescription === 'Iron');
  const ironSets = ironBag?.tourBagIronSets || [];
  const setNames = ironSets.map((set) => set.tourBagIronSetName?.toLocaleLowerCase());
  const [ironSetToEditCached, setIronSetToEditCached] = useState<TourBagIronSet | null>(null);
  const [showPreEditOfflineModal, setShowPreEditOfflineModal] = useState(false);
  const [showPostEditOfflineModal, setShowPostEditOfflineModal] = useState(false);

  const handleAddNewIronSet = async (ironSet: TourBagIronSet) => {
    const tourBag = ironBag ?? createEmptyTourBag('Iron', player!);
    if (isOnline) {
      setShowSpinner(true);
    }

    const updatedSets: TourBagIronSet[] = [...ironSets, ironSet];
    const updatedBag: TourBagMstrPayload = {
      ...tourBag,
      tourBagMstrId: tourBag?.tourBagMstrId,
      tourBagIronSets: updatedSets,
    };

    try {
      const action: Action = 'CREATE';
      await updateTourBag(updatedBag, action, ironSet.tourBagIronSetId);
      setIronSetToCreate(null); // exit creation mode
    } catch (error) {
      showToast({ message: 'Failed to Create Iron Set' });
      console.error('Failed to create new iron set: ', error);
      // TODO: Save error to ErrorLogRepo
      throw error;
    } finally {
      if (!isOnline) {
        setShowPostEditOfflineModal(true);
      } else {
        showToast({ message: 'Iron Set Created Successfully', showCheckmark: true });
        setShowSpinner(false);
      }
    }
  };

  const openOfflineEditModal = (iron: TourBagIronSet) => {
    setIronSetToEditCached(iron);
    setShowPreEditOfflineModal(true);
  };

  const showEditFormOfflineModal = () => {
    setShowPreEditOfflineModal(false);
    setIronSetToEdit(ironSetToEditCached);
  };

  const handleClosePostEditOfflineModal = () => {
    setShowPostEditOfflineModal(false);
  };

  const handleEditedIronSet = async (ironSet: TourBagIronSet) => {
    if (!ironBag) throw new Error('Missing iron bag');
    if (!ironSets) throw new Error('Missing iron sets');
    if (isOnline) {
      setShowSpinner(true);
    }

    const updatedSets: TourBagIronSet[] = ironSets.map((tourBagSet) => {
      if (ironSet.tourBagIronSetId === tourBagSet.tourBagIronSetId) {
        return {
          ...tourBagSet,
          ...ironSet,
        };
      }
      return tourBagSet;
    });
    const updatedBag: TourBagMstrPayload = {
      ...ironBag,
      tourBagMstrId: ironBag?.tourBagMstrId,
      tourBagIronSets: updatedSets,
    };

    try {
      const action: Action = 'UPDATE';
      await updateTourBag(updatedBag, action, ironSet.tourBagIronSetId);
      setIronSetToEdit(null); // exit edition upon success
    } catch (error) {
      showToast({ message: 'Failed to Edit Iron Set' });
      console.error('Failed to Edit Iron Set values: ', error);
      // TODO: Save error to ErrorLogRepo
      throw error;
    } finally {
      if (!isOnline) {
        setShowPostEditOfflineModal(true);
      } else {
        showToast({ message: 'Iron Set Edited Successfully', showCheckmark: true });
        setShowSpinner(false);
      }
    }
  };

  const handleDeleteIron = async () => {
    if (!ironBag) throw new Error('Missing iron bag');
    if (!ironSets) throw new Error('Missing Iron sets');

    const set = ironSets.find((set) => set.tourBagIronSetId === ironToDelete?.tourBagIronSetId);
    if (!set) throw new Error('Missing Set to delete');

    const iron = set.tourBagIronDetails.find((iron) => iron.tourBagIronDetailId === ironToDelete?.tourBagIronDetailId);
    if (!iron) throw new Error('Missing Iron to delete');
    setShowSpinner(true);

    const deletedBag = createDeletedTourBagIronDetail(iron);
    const updatedSet = {
      ...set,
      tourBagIronDetails: set.tourBagIronDetails.filter((tourBagIron) => tourBagIron.tourBagIronDetailId !== iron.tourBagIronDetailId),
    };
    const updatedBag: TourBagMstrPayload = {
      ...ironBag,
      tourBagIronSets: ironSets.map((currentSet) => (currentSet.tourBagIronSetId === set.tourBagIronSetId ? updatedSet : currentSet)),
    };

    try {
      // TODO: Offline not supported yet
      await TourBagService.saveDeletedTourBag(deletedBag);
      const action: Action = 'DELETE';
      await updateTourBag(updatedBag, action, iron.tourBagIronDetailId);
      setIronToDelete(null); // reset state
      showToast({ message: 'Iron Deleted Successfully', showCheckmark: true });
    } catch (error) {
      showToast({ message: 'Failed to Delete Iron' });
      console.error('Failed to Delete Iron club values: ', error);
      // TODO: Save error to ErrorLogRepo
      throw error;
    } finally {
      setShowSpinner(false);
    }
  };

  const handleRemoveIronFromSet = async () => {
    if (!ironBag) throw new Error('Missing iron bag');
    if (!ironSets) throw new Error('Missing Iron sets');

    const set = ironSets.find((set) => set.tourBagIronSetId === ironToRemove?.tourBagIronSetId);
    if (!set) throw new Error('Missing Set to delete');

    const iron = set.tourBagIronDetails.find((iron) => iron.tourBagIronDetailId === ironToRemove?.tourBagIronDetailId);
    if (!iron) throw new Error('Missing Iron to delete');
    setShowSpinner(true);

    // Remove Iron from set
    const updatedSet = {
      ...set,
      tourBagIronDetails: set.tourBagIronDetails.filter((tourBagIron) => tourBagIron.tourBagIronDetailId !== iron.tourBagIronDetailId),
    };
    // Add Set shared props and remove set id from updated iron
    const updatedIron: TourBagIronDetail = { ...iron, ...buildSetSharedProps(set, iron), tourBagIronSetId: null };
    // Add Iron to tourBagIronDetail Iron array, and remove the set id
    const updatedBag: TourBagMstrPayload = {
      ...ironBag,
      tourBagIronDetail: [...ironBag.tourBagIronDetail, updatedIron],
      tourBagIronSets: ironSets.map((currentSet) => (currentSet.tourBagIronSetId === set.tourBagIronSetId ? updatedSet : currentSet)),
    };

    try {
      // TODO: Offline not supported yet
      const action: Action = 'UPDATE';
      await updateTourBag(updatedBag, action, iron.tourBagIronDetailId);
      setIronToRemove(null); // reset state
      showToast({ message: 'Iron Removed Successfully', showCheckmark: true });
    } catch (error) {
      showToast({ message: 'Failed to Remove Iron' });
      console.error('Failed to Remove Iron club values: ', error);
      // TODO: Save error to ErrorLogRepo
      throw error;
    } finally {
      setShowSpinner(false);
    }
  };

  const handleDeleteSet = async () => {
    if (!ironBag) throw new Error('Missing iron bag');

    const set = ironSets.find((set) => set.tourBagIronSetId === setToDelete?.tourBagIronSetId);
    if (!set) throw new Error('Missing Set to delete');
    setShowSpinner(true);

    // Remove set from TourBag
    const updatedSets = ironSets.filter((currentSet) => currentSet.tourBagIronSetId !== set.tourBagIronSetId);
    // Update TourBag
    const updatedBag: TourBagMstrPayload = {
      ...ironBag,
      tourBagIronSets: updatedSets,
    };

    try {
      const action: Action = 'UPDATE';
      await updateTourBag(updatedBag, action, set.tourBagIronSetId);
      setSetToDelete(null); // reset state
      setShowDeleteSetModal(false);
      showToast({ message: 'Set Deleted Successfully', showCheckmark: true });
    } catch (error) {
      showToast({ message: 'Failed to Delete Set' });
      console.error('Failed to Delete Set club values: ', error);
      // TODO: Save error to ErrorLogRepo
      throw error;
    } finally {
      setShowSpinner(false);
    }
  };

  const closeIronHistoryModal = () => {
    setIronForHistory(null);
  };

  return (
    <>
      {/* Create Iron Set */}
      {ironSetToCreate !== null && (
        <AccordionShell>
          <EditSet
            title="Create Set"
            action="create"
            ironSet={ironSetToCreate}
            tourBagMstrId={ironBag?.tourBagMstrId}
            setNames={setNames}
            ironClubData={ironClubData}
            handDropdownData={handDropdownData}
            loftDropdownData={loftDropdownData}
            lieDropdownData={lieDropdownData}
            ironStaticDropdownData={ironStaticDropdownData}
            ironDynamicDropdownData={ironDynamicDropdownData}
            shaftDropdownData={shaftDropdownData}
            shaftLengthDropdownData={shaftLengthDropdownData}
            swingWtDropdownData={swingWtDropdownData}
            roundRibDropdownData={roundRibDropdownData}
            logoDropdownData={logoDropdownData}
            handleSave={handleAddNewIronSet}
            handleCancel={() => setIronSetToCreate(null)}
          />
        </AccordionShell>
      )}
      {/* Edit Iron Set */}
      {ironSetToEdit !== null && (
        <AccordionShell>
          <EditSet
            title="Edit Set"
            action="edit"
            ironSet={ironSetToEdit}
            tourBagMstrId={ironBag?.tourBagMstrId}
            setNames={setNames}
            ironClubData={ironClubData}
            handDropdownData={handDropdownData}
            loftDropdownData={loftDropdownData}
            lieDropdownData={lieDropdownData}
            ironStaticDropdownData={ironStaticDropdownData}
            ironDynamicDropdownData={ironDynamicDropdownData}
            shaftDropdownData={shaftDropdownData}
            shaftLengthDropdownData={shaftLengthDropdownData}
            swingWtDropdownData={swingWtDropdownData}
            roundRibDropdownData={roundRibDropdownData}
            logoDropdownData={logoDropdownData}
            handleSave={handleEditedIronSet}
            handleCancel={() => setIronSetToEdit(null)}
          />
        </AccordionShell>
      )}
      {/* Iron Set */}
      {showClubs &&
        ironSets
          .sort((setA, setB) => (setA.tourBagIronSetName || '').localeCompare(setB.tourBagIronSetName || ''))
          .sort((setA, setB) => {
            const setAHasActive = setA.tourBagIronDetails.some((iron) => iron.isActive);
            const setBHasActive = setB.tourBagIronDetails.some((iron) => iron.isActive);
            if (setAHasActive === setBHasActive) return 0; // Put sets with active irons first
            return setAHasActive ? -1 : 1;
          })
          .map((set, setIndex) => {
            const setClubsCount = set.tourBagIronDetails.length;
            const inBagCount = set.tourBagIronDetails.filter((iron) => iron.isActive).length;

            return (
              <Accordion
                key={set.tourBagIronSetId}
                index={setIndex}
                header={
                  <>
                    <div className={setStyles.headerRow}>
                      <span className={setStyles.setModel}>{set.tourBagIronSetName}</span>
                      <span className={setStyles.dot}>·</span>
                      <span className={setStyles.clubCount}>
                        {setClubsCount} {setClubsCount === 1 ? 'club' : 'clubs'}
                      </span>
                      {!isMobileResolution && <SetClubs amount={inBagCount} squareIcon={!inBagCount} className={`${clubStyles.inTheBag} ${setStyles.inBag}`} />}
                    </div>
                    {isMobileResolution && <SetClubs amount={inBagCount} squareIcon={!inBagCount} className={`${clubStyles.inTheBag} ${setStyles.inBag}`} />}
                  </>
                }
                showMenu={tourPageSettings?.iron === 'full' && isSyncComplete}
                showHistory={permissions?.tourBagSettings?.clubHistory ?? false}
                handleEdit={!isOnline ? () => openOfflineEditModal(set) : () => setIronSetToEdit(set)}
                handleCopy={() => setIronSetToCreate(set)}
                triggerDeleteModal={false}
                handleDelete={() => {
                  setShowDeleteSetModal(true);
                  setSetToDelete(set);
                }}
              >
                {/* Render irons inside the set */}
                {set.tourBagIronDetails
                  .sort((a, b) => (a.loftDigit + a.loftFraction).localeCompare(b.loftDigit + b.loftFraction))
                  .sort((a, b) => Number(b.isActive) - Number(a.isActive))
                  .map((iron, index) => {
                    return renderIronInSet({
                      iron,
                      set,
                      options: {
                        showMenu: isOnline && tourPageSettings?.iron === 'full' && isSyncComplete,
                        index,
                        onRemoveFromSet: (set: TourBagIronSet, iron: TourBagIronDetail) => {
                          setIronToRemove({ tourBagIronSetId: set.tourBagIronSetId, tourBagIronDetailId: iron.tourBagIronDetailId });
                          setShowRemoveFromSetModal(true);
                        },
                        onDelete: (set: TourBagIronSet, iron: TourBagIronDetail) => {
                          setIronToDelete({ tourBagIronSetId: set.tourBagIronSetId, tourBagIronDetailId: iron.tourBagIronDetailId });
                          setShowDeleteModal(true);
                        },
                        onFinalHWClick: (iron: TourBagIronDetail) => {
                          setFinalHWIron(iron);
                          setShowFinalHWModal(true);
                        },
                        onShowHistory: (iron: TourBagIronDetail) => {
                          setIronForHistory(iron);
                        },
                        showDivisionLine: index === setClubsCount - 1,
                        pageReadability: tourPageSettings?.iron,
                        defaultIcon: ironSetToEdit?.tourBagIronSetId === set.tourBagIronSetId,
                        showHistory: permissions?.tourBagSettings?.clubHistory ?? false,
                      },
                    });
                  })}

                {renderSet({ set, pageReadability: tourPageSettings?.iron })}
                <PhotoCaptureSection playerId={player?.playerId || 0} ownerRowGuid={set.rowGuid} headTypeId={headTypeMap['Iron']} imageMetadata={set.imageMetadata} isASetClub />
              </Accordion>
            );
          })}
      {showDeleteModal && (
        <DeleteModal
          handleConfirm={() => {
            setShowDeleteModal(false);
            handleDeleteIron();
          }}
          handleCancel={() => {
            setIronToDelete(null);
            setShowDeleteModal(false);
          }}
        />
      )}
      {showRemoveFromSetModal && (
        <DeleteModal
          confirmLabel="Remove"
          title="Remove Iron from Set"
          body="Are you sure you want to remove this Iron from the Set?"
          handleConfirm={() => {
            setShowRemoveFromSetModal(false);
            handleRemoveIronFromSet();
          }}
          handleCancel={() => {
            setIronToRemove(null);
            setShowRemoveFromSetModal(false);
          }}
        />
      )}
      {showFinalHWModal && finalHWIron && <FinalHeadWeightModal showFinalHWModal={showFinalHWModal} closeFinalHWModal={() => setShowFinalHWModal(false)} iron={finalHWIron} />}
      {showDeleteSetModal && setToDelete && (
        <DeleteSetModal
          showDeleteSet={showDeleteSetModal}
          closeDeleteSet={() => {
            setSetToDelete(null);
            setShowDeleteSetModal(false);
          }}
          set={setToDelete}
          onDeleteSet={handleDeleteSet}
        />
      )}
      {ironForHistory && (
        <ClubHistory
          tourBagMstrId={ironForHistory.tourBagMstrId}
          rowGuid={ironForHistory.rowGuid}
          headTypeId={headTypeMap['Iron']}
          player={player}
          model={ironForHistory.headModelName}
          closeModal={closeIronHistoryModal}
        />
      )}
      <OfflineModal onCancel={() => setShowPreEditOfflineModal(false)} onClose={showEditFormOfflineModal} isOpen={showPreEditOfflineModal} modalType={'IsOffline'} />
      <OfflineModal onCancel={() => {}} onClose={handleClosePostEditOfflineModal} isOpen={showPostEditOfflineModal} modalType={'ConfirmSave'} />
    </>
  );
}

export default IronSet;
